/*
 * theme-product.js — Decoupled Product-specific modules from theme.js
 * Modules: theme.Variants, theme.ProductMedia, theme.StoreAvailability, theme.Product
 */
(function() {
  'use strict';
  window.theme = window.theme || {};

 /*
   Options:
     container
     enableHistoryState - enable when on single product page to update URL
     singleOptionSelector - selector for individual variant option (e.g. 'Blue' or 'Small')
     originalSelectorId - selector for base variant selector (visually hidden)
     variants - JSON parsed object of product variant info
  */
 theme.Variants = (function() {

  function Variants(options) {
   this.container = options.container;
   this.variants = options.variants;
   this.singleOptionSelector = options.singleOptionSelector;
   this.originalSelectorId = options.originalSelectorId;
   this.enableHistoryState = options.enableHistoryState;
   this.dynamicVariantsEnabled = options.dynamicVariantsEnabled;
   this.currentlySelectedValues = this._getCurrentOptions();
   this.currentVariant = this._getVariantFromOptions();

   this.container.querySelectorAll(this.singleOptionSelector).forEach(el => {
    el.addEventListener('change', this._onSelectChange.bind(this));
   });
  }

  Variants.prototype = Object.assign({}, Variants.prototype, {

   _getCurrentOptions: function() {
    var result = [];

    this.container.querySelectorAll(this.singleOptionSelector).forEach(el => {
     var type = el.getAttribute('type');

     if (type === 'radio' || type === 'checkbox') {
      if (el.checked) {
       result.push({
        value: el.value,
        index: el.dataset.index
       });
      }
     } else {
      result.push({
       value: el.value,
       index: el.dataset.index
      });
     }
    });

    // remove any unchecked input values if using radio buttons or checkboxes
    result = theme.utils.compact(result);

    return result;
   },

   // Pull the number out of the option index name, e.g. 'option1' -> 1
   _numberFromOptionKey: function(key) {
    return parseInt(key.substr(-1));
   },

   // Options should be ordered from highest to lowest priority. Make sure that priority
   // is represented using weighted values when finding best match
   _getWeightedOptionMatchCount: function(variant) {
    return this._getCurrentOptions().reduce((count, { value, index }) => {
     const optionIndex = this._numberFromOptionKey(index);
     const weightedCount = 4 - optionIndex; // The lower the index, the better the match we have
     return variant[index] === value ? count + weightedCount : count;
    }, 0)
   },

   _getFullMatch(needsToBeAvailable) {
    const currentlySelectedOptions = this._getCurrentOptions();
    const variants = this.variants;

    return variants.find(variant => {
     const isMatch = currentlySelectedOptions.every(({ value, index }) => {
      return variant[index] === value;
     });

     if (needsToBeAvailable) {
      return isMatch && variant.available;
     } else {
      return isMatch;
     }
    });
   },

   // Find a variant that is available and best matches last selected option
   _getClosestAvailableMatch: function(lastSelectedOption) {
    if (!lastSelectedOption) return null;

    const currentlySelectedOptions = this._getCurrentOptions();
    const variants = this.variants;

    const potentialAvailableMatches = lastSelectedOption && variants.filter(variant => {
     return currentlySelectedOptions
      .filter(
       // Only match based selected options that are equal and preceeding the last selected option
       ({ value, index }) => this._numberFromOptionKey(index) <= this._numberFromOptionKey(lastSelectedOption.index)
      ).every(({ value, index }) => {
       // Variant needs to have options that match the current and preceeding selection options
       return variant[index] === value;
      }) && variant.available
    });

    return potentialAvailableMatches.reduce((bestMatch, variant) => {
     // If this is the first potential match we've found, store it as the best match
     if (bestMatch === null) return variant;

     // If this is not the first potential match, compare the number of options our current best match has in common
     // compared to the next contender.
     const bestMatchCount = this._getWeightedOptionMatchCount(bestMatch, lastSelectedOption);
     const newCount = this._getWeightedOptionMatchCount(variant, lastSelectedOption);

     return newCount > bestMatchCount ? variant : bestMatch;
    }, null);
   },

   _getVariantFromOptions: function(lastSelectedOption) {
    const availableFullMatch = this._getFullMatch(true);
    const closestAvailableMatch = this._getClosestAvailableMatch(lastSelectedOption);
    const fullMatch = this._getFullMatch(false);

    if (this.dynamicVariantsEnabled) {
     // Add some additional smarts to variant matching if Dynamic Variants are enabled
     return availableFullMatch || closestAvailableMatch || fullMatch || null;
    } else {
     // Only return a full match or null (variant doesn't exist) if Dynamic Variants are disabled
     return fullMatch || null;
    }

   },

   _updateInputState: function(variant, el) {
    return (input) => {
     if (variant === null) return;

     const index = input.dataset.index;
     const value = input.value;
     const type = input.getAttribute('type');

     if (type === 'radio' || type === 'checkbox') {
      input.checked = variant[index] === value
      if (input.checked) {
       input.dispatchEvent(new Event('updateSwatch'));
      }
     } else {
      input.value = variant[index];
     }
    }
   },

   _onSelectChange: function({ srcElement }) {
    const optionSelectElements = this.container.querySelectorAll(this.singleOptionSelector);

    // Get the best variant based on the current selection + last selected element
    const variant = this._getVariantFromOptions({
     index: srcElement.dataset.index,
     value: srcElement.value
    });

    // Update DOM option input states based on the variant that was found
    optionSelectElements.forEach(this._updateInputState(variant, srcElement))

    // Make sure our currently selected values are up to date after updating state of DOM
    const currentlySelectedValues = this.currentlySelectedValues = this._getCurrentOptions();

    const detail = {
     variant,
     currentlySelectedValues,
     value: srcElement.value,
     index: srcElement.parentElement.dataset.index
    }

    this.container.dispatchEvent(new CustomEvent('variantChange', { detail }));
    document.dispatchEvent(new CustomEvent('variant:change', { detail }));

    if (!variant) {
     return;
    }

    this._updateMasterSelect(variant);
    this._updateImages(variant);
    this._updatePrice(variant);
    this._updateUnitPrice(variant);
    this._updateSKU(variant);
    this.currentVariant = variant;

    if (this.enableHistoryState) {
     this._updateHistoryState(variant);
    }
   },

   _updateImages: function(variant) {
    if (!variant.featured_image) {
     return;
    }

    this.container.dispatchEvent(new CustomEvent('variantImageChange', {
     detail: {
      variant: variant
     }
    }));
   },

   _updatePrice: function(variant) {
    if (this.currentVariant && variant.price === this.currentVariant.price && variant.compare_at_price === this.currentVariant.compare_at_price) {
     return;
    }

    this.container.dispatchEvent(new CustomEvent('variantPriceChange', {
     detail: {
      variant: variant
     }
    }));
   },

   _updateUnitPrice: function(variant) {
    if (this.currentVariant && variant.unit_price === this.currentVariant.unit_price) {
     return;
    }

    this.container.dispatchEvent(new CustomEvent('variantUnitPriceChange', {
     detail: {
      variant: variant
     }
    }));
   },

   _updateSKU: function(variant) {
    if (this.currentVariant && variant.sku === this.currentVariant.sku) {
     return;
    }

    this.container.dispatchEvent(new CustomEvent('variantSKUChange', {
     detail: {
      variant: variant
     }
    }));
   },

   _updateHistoryState: function(variant) {
    if (!history.replaceState || !variant) {
     return;
    }

    var newurl = window.location.protocol + '//' + window.location.host + window.location.pathname + '?variant=' + variant.id;
    window.history.replaceState({ path: newurl }, '', newurl);
   },

   _updateMasterSelect: function(variant) {
    let masterSelect = this.container.querySelector(this.originalSelectorId);
    if (!masterSelect) return;

    masterSelect.value = variant.id;
    // Force a change event so Shop Pay installments works after a variant is changed
    masterSelect.dispatchEvent(new Event('change', { bubbles: true }));
   }
  });

  return Variants;
 })();

 window.vimeoApiReady = function() {
  theme.config.vimeoLoading = true

  // Because there's no way to check for the Vimeo API being loaded
  // asynchronously, we use this terrible timeout to wait for it being ready
  checkIfVimeoIsReady()
   .then(function() {
    theme.config.vimeoReady = true
    theme.config.vimeoLoading = false
    document.dispatchEvent(new CustomEvent('vimeoReady'))
   })
 }

 function checkIfVimeoIsReady() {
  let wait
  let timeout

  return new Promise((resolve, reject) => {
   wait = setInterval(function() {
    if (!Vimeo) {
     return
    }

    clearInterval(wait)
    clearTimeout(timeout)
    resolve()
   }, 500)

   timeout = setTimeout(function() {
    clearInterval(wait)
    reject()
   }, 4000) // subjective. test up to 8 times over 4 seconds
  })
 }


 theme.ProductMedia = (function() {
  var modelJsonSections = {};
  var models = {};
  var xrButtons = {};

  var selectors = {
   mediaGroup: '[data-product-single-media-group]',
   xrButton: '[data-shopify-xr]'
  };

  function init(modelViewerContainers, sectionId) {
   modelJsonSections[sectionId] = {
    loaded: false
   };

   modelViewerContainers.forEach(function(container, index) {
    var mediaId = container.dataset.mediaId;
    var modelViewerElement = container.querySelector('model-viewer');
    var modelId = modelViewerElement.dataset.modelId;

    if (index === 0) {
     var mediaGroup = container.closest(selectors.mediaGroup);
     var xrButton = mediaGroup.querySelector(selectors.xrButton);
     xrButtons[sectionId] = {
      element: xrButton,
      defaultId: modelId
     };
    }

    models[mediaId] = {
     modelId: modelId,
     sectionId: sectionId,
     container: container,
     element: modelViewerElement
    };

   });

   window.Shopify.loadFeatures([
    {
     name: 'shopify-xr',
     version: '1.0',
     onLoad: setupShopifyXr
    },
    {
     name: 'model-viewer-ui',
     version: '1.0',
     onLoad: setupModelViewerUi
    }
   ]);

   theme.LibraryLoader.load('modelViewerUiStyles');
  }

  function setupShopifyXr(errors) {
   if (errors) return;

   if (!window.ShopifyXR) {
    document.addEventListener('shopify_xr_initialized', function() {
     setupShopifyXr();
    });
    return;
   }

   for (var sectionId in modelJsonSections) {
    if (modelJsonSections.hasOwnProperty(sectionId)) {
     var modelSection = modelJsonSections[sectionId];

     if (modelSection.loaded) continue;

     var modelJson = document.querySelector('#ModelJson-' + sectionId);

     window.ShopifyXR.addModels(JSON.parse(modelJson.innerHTML));
     modelSection.loaded = true;
    }
   }
   window.ShopifyXR.setupXRElements();
  }

  function setupModelViewerUi(errors) {
   if (errors) return;

   for (var key in models) {
    if (models.hasOwnProperty(key)) {
     var model = models[key];
     if (!model.modelViewerUi && Shopify) {
      model.modelViewerUi = new Shopify.ModelViewerUI(model.element);
     }
     setupModelViewerListeners(model);
    }
   }
  }

  function setupModelViewerListeners(model) {
   var xrButton = xrButtons[model.sectionId];

   model.container.addEventListener('mediaVisible', function() {
    xrButton.element.setAttribute('data-shopify-model3d-id', model.modelId);
    if (theme.config.isTouch) return;
    model.modelViewerUi.play();
   });

   model.container.addEventListener('mediaHidden', function() {
    xrButton.element.setAttribute('data-shopify-model3d-id', xrButton.defaultId);
    model.modelViewerUi.pause();
   });

   model.container.addEventListener('xrLaunch', function() {
    model.modelViewerUi.pause();
   });
  }

  function removeSectionModels(sectionId) {
   for (var key in models) {
    if (models.hasOwnProperty(key)) {
     var model = models[key];
     if (model.sectionId === sectionId) {
      delete models[key];
     }
    }
   }
   delete modelJsonSections[sectionId];
  }

  return {
   init: init,
   removeSectionModels: removeSectionModels
  };
 })();


 theme.StoreAvailability = (function() {
  var selectors = {
   drawerOpenBtn: '.js-drawer-open-availability',
   modalOpenBtn: '.js-modal-open-availability',
   productTitle: '[data-availability-product-title]'
  };

  function StoreAvailability(container) {
   this.container = container;
   this.baseUrl = container.dataset.baseUrl;
   this.productTitle = container.dataset.productName;
  }

  StoreAvailability.prototype = Object.assign({}, StoreAvailability.prototype, {
   updateContent: function(variantId) {
    var variantSectionUrl =
     this.baseUrl +
     '/variants/' +
     variantId +
     '/?section_id=store-availability';

    var self = this;

    fetch(variantSectionUrl)
     .then(function(response) {
      return response.text();
     })
     .then(function(html) {
      if (html.trim() === '') {
       this.container.innerHTML = '';
       return;
      }

      self.container.innerHTML = html;
      self.container.innerHTML = self.container.firstElementChild.innerHTML;

      // Setup drawer if have open button
      if (self.container.querySelector(selectors.drawerOpenBtn)) {
       self.drawer = new theme.Drawers('StoreAvailabilityDrawer', 'availability');
      }

      // Setup modal if have open button
      if (self.container.querySelector(selectors.modalOpenBtn)) {
       self.modal = new theme.Modals('StoreAvailabilityModal', 'availability');
      }

      var title = self.container.querySelector(selectors.productTitle);
      if (title) {
       title.textContent = self.productTitle;
      }
     });
   }
  });

  return StoreAvailability;
 })();


 theme.Product = (function() {
  var videoObjects = {};

  var classes = {
   onSale: 'on-sale',
   disabled: 'disabled',
   isModal: 'is-modal',
   loading: 'loading',
   loaded: 'loaded',
   hidden: 'hide',
   interactable: 'video-interactable',
   visuallyHide: 'visually-invisible',
   lowInventory: 'inventory--low',
  };

  var selectors = {
   productVideo: '.product__video',
   videoParent: '.product__video-wrapper',
   slide: '.product-main-slide',
   currentSlide: '.is-selected',
   startingSlide: '.starting-slide',
   variantType: '.variant-wrapper',
   blocks: '[data-product-blocks]',
   blocksHolder: '[data-blocks-holder]',
   dynamicVariantsEnabled: '[data-dynamic-variants-enabled]',
  };

  function Product(container) {
   this.container = container;
   var sectionId = this.sectionId = container.getAttribute('data-section-id');
   var productId = this.productId = container.getAttribute('data-product-id');

   this.inModal = (container.dataset.modal === 'true');
   this.modal;

   this.settings = {
    enableHistoryState: container.dataset.history || false,
    namespace: '.product-' + sectionId,
    inventory: false,
    inventoryThreshold: 10,
    modalInit: false,
    hasImages: true,
    imageSetName: null,
    imageSetIndex: null,
    currentImageSet: null,
    imageSize: '620x',
    currentSlideIndex: 0,
    videoLooping: container.dataset.videoLooping
   };

   // Overwrite some settings when loaded in modal
   if (this.inModal) {
    this.settings.enableHistoryState = false;
    this.settings.namespace = '.product-' + sectionId + '-modal';
    this.modal = document.getElementById('QuickShopModal-' + productId);
   }

   this.selectors = {
    variantsJson: '[data-variant-json]',
    currentVariantJson: '[data-current-variant-json]',
    form: '.product-single__form',

    media: '[data-product-media-type-model]',
    closeMedia: '.product-single__close-media',
    photoThumbs: '[data-product-thumb]',
    thumbSlider: '[data-product-thumbs]',
    thumbScroller: '.product__thumbs--scroller',
    mainSlider: '[data-product-photos]',
    imageContainer: '[data-product-images]',
    productImageMain: '[data-product-image-main]',

    priceWrapper: '[data-product-price-wrap]',
    price: '[data-product-price]',
    comparePrice: '[data-compare-price]',
    savePrice: '[data-save-price]',
    priceA11y: '[data-a11y-price]',
    comparePriceA11y: '[data-compare-price-a11y]',
    unitWrapper: '[data-unit-price-wrapper]',
    unitPrice: '[data-unit-price]',
    unitPriceBaseUnit: '[data-unit-base]',
    sku: '[data-sku]',
    inventory: '[data-product-inventory]',
    incomingInventory: '[data-incoming-inventory]',
    colorLabel: '[data-variant-color-label]',

    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',

    originalSelectorId: '[data-product-select]',
    singleOptionSelector: '[data-variant-input]',
    variantColorSwatch: '.variant__input--color-swatch',
    dynamicVariantsEnabled: '[data-dynamic-variants-enabled]',

    availabilityContainer: '[data-store-availability-holder]'
   };

   this.cacheElements();

   this.firstProductImage = this.cache.mainSlider.querySelector('img');

   if (!this.firstProductImage) {
    this.settings.hasImages = false;
   }

   var dataSetEl = this.cache.mainSlider.querySelector('[data-set-name]');
   if (dataSetEl) {
    this.settings.imageSetName = dataSetEl.dataset.setName;
   }

   this.init();
  }

  Product.prototype = Object.assign({}, Product.prototype, {
   init: function() {
    if (this.inModal) {
     this.container.classList.add(classes.isModal);
     document.addEventListener('modalOpen.QuickShopModal-' + this.productId, this.openModalProduct.bind(this));
     document.addEventListener('modalClose.QuickShopModal-' + this.productId, this.closeModalProduct.bind(this));
    }

    if (!this.inModal) {
     this.formSetup();
     this.productSetup();
     this.videoSetup();
     this.initProductSlider();
     this.customMediaListners();
     this.addIdToRecentlyViewed();
    }
   },

   cacheElements: function() {
    this.cache = {
     form: this.container.querySelector(this.selectors.form),
     mainSlider: this.container.querySelector(this.selectors.mainSlider),
     thumbSlider: this.container.querySelector(this.selectors.thumbSlider),
     thumbScroller: this.container.querySelector(this.selectors.thumbScroller),
     productImageMain: this.container.querySelector(this.selectors.productImageMain),

     // Price-related
     priceWrapper: this.container.querySelector(this.selectors.priceWrapper),
     comparePriceA11y: this.container.querySelector(this.selectors.comparePriceA11y),
     comparePrice: this.container.querySelector(this.selectors.comparePrice),
     price: this.container.querySelector(this.selectors.price),
     savePrice: this.container.querySelector(this.selectors.savePrice),
     priceA11y: this.container.querySelector(this.selectors.priceA11y)
    };
   },

   formSetup: function() {
    this.initQtySelector();
    this.initAjaxProductForm();
    this.availabilitySetup();
    this.initVariants();

    // We know the current variant now so setup image sets
    if (this.settings.imageSetName) {
     this.updateImageSet();
    }
   },

   availabilitySetup: function() {
    var container = this.container.querySelector(this.selectors.availabilityContainer);
    if (container) {
     this.storeAvailability = new theme.StoreAvailability(container);
    }
   },

   productSetup: function() {
    this.setImageSizes();
    this.initImageZoom();
    this.initModelViewerLibraries();
    this.initShopifyXrLaunch();
   },

   setImageSizes: function() {
    if (!this.settings.hasImages) {
     return;
    }

    // Get srcset image src, works on most modern browsers
    // otherwise defaults to settings.imageSize
    var currentImage = this.firstProductImage.currentSrc;

    if (currentImage) {
     this.settings.imageSize = theme.Images.imageSize(currentImage);
    }
   },

   addIdToRecentlyViewed: function() {
    var handle = this.container.getAttribute('data-product-handle');
    var url = this.container.getAttribute('data-product-url');
    var aspectRatio = this.container.getAttribute('data-aspect-ratio');
    var featuredImage = this.container.getAttribute('data-img-url');

    // Remove current product if already in set of recent
    if (theme.recentlyViewed.recent.hasOwnProperty(handle)) {
     delete theme.recentlyViewed.recent[handle];
    }

    // Add it back to the end
    theme.recentlyViewed.recent[handle] = {
     url: url,
     aspectRatio: aspectRatio,
     featuredImage: featuredImage
    };

    if (theme.config.hasLocalStorage) {
     window.localStorage.setItem('theme-recent', JSON.stringify(theme.recentlyViewed.recent));
    }
   },

   initVariants: function() {
    var variantJson = this.container.querySelector(this.selectors.variantsJson);

    if (!variantJson) {
     return;
    }

    this.variantsObject = JSON.parse(variantJson.innerHTML);
    var dynamicVariantsEnabled = !!this.container.querySelector(selectors.dynamicVariantsEnabled)

    var options = {
     container: this.container,
     enableHistoryState: this.settings.enableHistoryState,
     singleOptionSelector: this.selectors.singleOptionSelector,
     originalSelectorId: this.selectors.originalSelectorId,
     variants: this.variantsObject,
     dynamicVariantsEnabled
    };

    var swatches = this.container.querySelectorAll(this.selectors.variantColorSwatch);
    if (swatches.length) {
     swatches.forEach(swatch => {
      swatch.addEventListener('updateSwatch', function(evt) {
       var color = swatch.dataset.colorName;
       var index = swatch.dataset.colorIndex;
       this.updateColorName(color, index);
      }.bind(this))
     });
    }

    this.variants = new theme.Variants(options);

    // Product availability on page load
    if (this.storeAvailability) {
     var variant_id = this.variants.currentVariant ? this.variants.currentVariant.id : this.variants.variants[0].id;

     this.storeAvailability.updateContent(variant_id);
     this.container.on('variantChange' + this.settings.namespace, this.updateAvailability.bind(this));
    }

    this.container.on('variantChange' + this.settings.namespace, this.updateCartButton.bind(this));
    this.container.on('variantImageChange' + this.settings.namespace, this.updateVariantImage.bind(this));
    this.container.on('variantPriceChange' + this.settings.namespace, this.updatePrice.bind(this));
    this.container.on('variantUnitPriceChange' + this.settings.namespace, this.updateUnitPrice.bind(this));

    if (this.container.querySelector(this.selectors.sku)) {
     this.container.on('variantSKUChange' + this.settings.namespace, this.updateSku.bind(this));
    }

    var inventoryEl = this.container.querySelector(this.selectors.inventory);
    if (inventoryEl) {
     this.settings.inventory = true;
     this.settings.inventoryThreshold = inventoryEl.dataset.threshold;

     // Update inventory on page load
     this.updateInventory({ detail: { variant: this.variants.currentVariant } });

     this.container.on('variantChange' + this.settings.namespace, this.updateInventory.bind(this));
    }

    // Update individual variant availability on each selection
    if (dynamicVariantsEnabled) {
     var currentVariantJson = this.container.querySelector(this.selectors.currentVariantJson);

     if (currentVariantJson) {
      var variantType = this.container.querySelector(selectors.variantType);

      if (variantType) {
       new theme.VariantAvailability({
        container: this.container,
        namespace: this.settings.namespace,
        type: variantType.dataset.type,
        variantsObject: this.variantsObject,
        currentVariantObject: JSON.parse(currentVariantJson.innerHTML)
       });
      }
     }
    }

    // image set names variant change listeners
    if (this.settings.imageSetName) {
     var variantWrapper = this.container.querySelector('.variant-input-wrap[data-handle="' + this.settings.imageSetName + '"]');
     if (variantWrapper) {
      this.settings.imageSetIndex = variantWrapper.dataset.index;
      this.container.on('variantChange' + this.settings.namespace, this.updateImageSet.bind(this))
     } else {
      this.settings.imageSetName = null;
     }
    }
   },

   initQtySelector: function() {
    this.container.querySelectorAll('.js-qty__wrapper').forEach(el => {
     new theme.QtySelector(el, {
      namespace: '.product'
     });
    });
   },

   initAjaxProductForm: function() {
    new theme.AjaxProduct(this.cache.form);
   },

   /*============================================================================
     Variant change methods
   ==============================================================================*/
   updateColorName: function(color, index) {
    // Updates on radio button change, not variant.js
    this.container.querySelector(this.selectors.colorLabel + `[data-index="${index}"`).textContent = color;
   },

   updateCartButton: function(evt) {
    var variant = evt.detail.variant;
    var cartBtn = this.container.querySelector(this.selectors.addToCart);
    var cartBtnText = this.container.querySelector(this.selectors.addToCartText);

    if (!cartBtn) return;

    if (variant) {
     if (variant.available) {
      // Available, enable the submit button and change text
      cartBtn.classList.remove(classes.disabled);
      cartBtn.disabled = false;
      var defaultText = cartBtnText.dataset.defaultText;
      cartBtnText.textContent = defaultText;
     } else {
      // Sold out, disable the submit button and change text
      cartBtn.classList.add(classes.disabled);
      cartBtn.disabled = true;
      cartBtnText.textContent = theme.strings.soldOut;
     }
    } else {
     // The variant doesn't exist, disable submit button
     cartBtn.classList.add(classes.disabled);
     cartBtn.disabled = true;
     cartBtnText.textContent = theme.strings.unavailable;
    }
   },

   updatePrice: function(evt) {
    var variant = evt.detail.variant;

    if (variant) {
     // Regular price
     this.cache.price.innerHTML = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);

     // Sale price, if necessary
     if (variant.compare_at_price > variant.price) {
      this.cache.comparePrice.innerHTML = theme.Currency.formatMoney(variant.compare_at_price, theme.settings.moneyFormat);
      this.cache.priceWrapper.classList.remove(classes.hidden);
      this.cache.price.classList.add(classes.onSale);
      this.cache.comparePriceA11y.setAttribute('aria-hidden', 'false');
      this.cache.priceA11y.setAttribute('aria-hidden', 'false');

      var savings = variant.compare_at_price - variant.price;

      if (theme.settings.saveType == 'percent') {
       savings = Math.round(((savings) * 100) / variant.compare_at_price) + '%';
      } else {
       savings = theme.Currency.formatMoney(savings, theme.settings.moneyFormat);
      }

      this.cache.savePrice.classList.remove(classes.hidden);
      this.cache.savePrice.innerHTML = theme.strings.savePrice.replace('[saved_amount]', savings);
     } else {
      if (this.cache.priceWrapper) {
       this.cache.priceWrapper.classList.add(classes.hidden);
      }
      this.cache.savePrice.classList.add(classes.hidden);
      this.cache.price.classList.remove(classes.onSale);
      if (this.cache.comparePriceA11y) {
       this.cache.comparePriceA11y.setAttribute('aria-hidden', 'true');
      }
      this.cache.priceA11y.setAttribute('aria-hidden', 'true');
     }
    }
   },

   updateUnitPrice: function(evt) {
    var variant = evt.detail.variant;

    if (variant && variant.unit_price) {
     this.container.querySelector(this.selectors.unitPrice).innerHTML = theme.Currency.formatMoney(variant.unit_price, theme.settings.moneyFormat);
     this.container.querySelector(this.selectors.unitPriceBaseUnit).innerHTML = theme.Currency.getBaseUnit(variant);
     this.container.querySelector(this.selectors.unitWrapper).classList.remove(classes.hidden);
    } else {
     this.container.querySelector(this.selectors.unitWrapper).classList.add(classes.hidden);
    }
   },

   imageSetArguments: function(variant) {
    var variant = variant ? variant : (this.variants ? this.variants.currentVariant : null);
    if (!variant) return;

    var setValue = this.settings.currentImageSet = this.getImageSetName(variant[this.settings.imageSetIndex]);
    var set = this.settings.imageSetName + '_' + setValue;

    // Always start on index 0
    this.settings.currentSlideIndex = 0;

    // Return object that adds cellSelector to mainSliderArgs
    return {
     cellSelector: '[data-group="' + set + '"]',
     imageSet: set,
     initialIndex: this.settings.currentSlideIndex
    }
   },

   updateImageSet: function(evt) {
    // If called directly, use current variant
    var variant = evt ? evt.detail.variant : (this.variants ? this.variants.currentVariant : null);
    if (!variant) {
     return;
    }

    var setValue = this.getImageSetName(variant[this.settings.imageSetIndex]);

    // Already on the current image group
    if (this.settings.currentImageSet === setValue) {
     return;
    }

    this.initProductSlider(variant);
   },

   // Show/hide thumbnails based on current image set
   updateImageSetThumbs: function(set) {
    this.cache.thumbSlider.querySelectorAll('.product__thumb-item').forEach(thumb => {
     thumb.classList.toggle(classes.hidden, thumb.dataset.group !== set);
    });
   },

   getImageSetName: function(string) {
    return string.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-$/, '').replace(/^-/, '');
   },

   updateSku: function(evt) {
    var variant = evt.detail.variant;
    var newSku = '';

    if (variant) {
     if (variant.sku) {
      newSku = variant.sku;
     }

     this.container.querySelector(this.selectors.sku).textContent = newSku;
    }
   },

   updateInventory: function(evt) {
    var variant = evt.detail.variant;

    // Override to display in stock message for sold out variants with policy set to continue
    // Must be set to true in order to display in stock message for sold out variants
    const inStockForOOSAndContinueSelling = false;

    if (!variant) {
     // Variant is unavailable
     // So we want to hide both the inventory quantity + incoming transfer notice
     this.toggleInventoryQuantity('hidden', false);
     this.toggleIncomingInventory(false);
     return;
    }

    // Inventory management is nil
    // So we want to hide the low inventory message but show an In stock status
    // And hide the incoming transfer notice
    if (!variant.inventory_management) {
     this.toggleInventoryQuantity('visible', false);
     this.toggleIncomingInventory(false);
     return;
    }

    if (variant.inventory_management === 'shopify' && window.inventories && window.inventories[this.productId]) {
     const variantInventoryObject = window.inventories[this.productId][variant.id];

     const { quantity, policy, incoming, next_incoming_date } = variantInventoryObject || {};

     this.toggleInventoryQuantity(undefined, quantity);

     if (inStockForOOSAndContinueSelling) {
      if (quantity <= 0 && policy === 'continue') {
       this.toggleInventoryQuantity('visible', false);
       this.toggleIncomingInventory(false);
       return;
      }
     }

     if ((incoming && !variant.available) || (quantity <= 0 && policy === 'continue')) {
      this.toggleIncomingInventory(true, next_incoming_date, policy);
     } else {
      this.toggleIncomingInventory(false);
     }
    }
   },

   updateAvailability: function(evt) {
    var variant = evt.detail.variant;
    if (!variant) {
     return;
    }

    this.storeAvailability.updateContent(variant.id);
   },

   toggleInventoryQuantity: function(state = undefined, quantity) {
    const productInventoryEl = this.container.querySelector(this.selectors.inventory);
    const inventorySalesPoint = productInventoryEl.closest('.sales-point');

    if (state && state === 'hidden') {
     // variant is unavailable
     // hide and return
     if (inventorySalesPoint) {
      inventorySalesPoint.classList.add(classes.hidden);
     }

     return;
    }

    let showLowInventoryMessage = false;

    // Check if we should show low inventory message
    if (parseInt(quantity) <= parseInt(this.settings.inventoryThreshold) && parseInt(quantity) > 0) {
     showLowInventoryMessage = true;
    }

    if (parseInt(quantity) > 0 || (state && state === 'visible')) {
     if (showLowInventoryMessage) {
      productInventoryEl.parentNode.classList.add(classes.lowInventory);
      if (quantity > 1) {
       productInventoryEl.textContent = theme.strings.otherStockLabel.replace('[count]', quantity);
      } else {
       productInventoryEl.textContent = theme.strings.oneStockLabel.replace('[count]', quantity);
      }
     } else {
      productInventoryEl.parentNode.classList.remove(classes.lowInventory);
      productInventoryEl.textContent = theme.strings.inStockLabel;
     }

     if (inventorySalesPoint) {
      inventorySalesPoint.classList.remove(classes.hidden);
     }

    } else {
     if (inventorySalesPoint) {
      inventorySalesPoint.classList.add(classes.hidden);
     }
    }
   },

   toggleIncomingInventory: function(showIncomingInventory, incomingInventoryDate, policy) {

    const incomingInventoryEl = this.container.querySelector(this.selectors.incomingInventory);
    const incomingInventoryIcon = incomingInventoryEl.querySelector('.icon-and-text');

    if (!incomingInventoryEl) return;

    const incomingInventoryBlockEnabled = incomingInventoryEl.dataset.enabled === 'true';
    const textEl = incomingInventoryEl.querySelector('.js-incoming-text');

    // If incoming inventory block is disabled, hide it
    if (!incomingInventoryBlockEnabled) {
     incomingInventoryEl.classList.add(classes.hidden);
     return;
    }

    if (showIncomingInventory) {
     if (incomingInventoryDate) {
      textEl.textContent = theme.strings.willBeInStockAfter.replace('[date]', incomingInventoryDate);
      incomingInventoryEl.classList.remove(classes.hidden);
     } else {
      textEl.textContent = theme.strings.waitingForStock;
      incomingInventoryEl.classList.remove(classes.hidden);
     }

     // When OOS and incoming inventory and continue selling disabled, update icon to low inventory
     if (incomingInventoryIcon) {
      if (policy !== 'continue') {
       incomingInventoryIcon.classList.add(classes.lowInventory);
      } else {
       incomingInventoryIcon.classList.remove(classes.lowInventory);
      }
     }

    } else {
     incomingInventoryEl.classList.add(classes.hidden);
    }
   },

   /*============================================================================
     Product videos
   ==============================================================================*/
   videoSetup: function() {
    var productVideos = this.cache.mainSlider.querySelectorAll(selectors.productVideo);

    if (!productVideos.length) {
     return false;
    }

    productVideos.forEach(vid => {
     var type = vid.dataset.videoType;
     if (type === 'youtube') {
      this.initYoutubeVideo(vid);
     } else if (type === 'vimeo') {
      this.initVimeoVideo(vid);
     } else if (type === 'mp4') {
      this.initMp4Video(vid);
     }
    });
   },

   initYoutubeVideo: function(div) {
    videoObjects[div.id] = new theme.YouTube(
     div.id,
     {
      videoId: div.dataset.videoId,
      videoParent: selectors.videoParent,
      autoplay: false, // will handle this in callback
      style: div.dataset.videoStyle,
      loop: div.dataset.videoLoop,
      events: {
       onReady: this.youtubePlayerReady.bind(this),
       onStateChange: this.youtubePlayerStateChange.bind(this)
      }
     }
    );
   },

   initVimeoVideo: function(div) {
    videoObjects[div.id] = new theme.VimeoPlayer(
     div.id,
     div.dataset.videoId,
     {
      videoParent: selectors.videoParent,
      autoplay: false,
      style: div.dataset.videoStyle,
      loop: div.dataset.videoLoop,
     }
    )
   },

   // Comes from YouTube SDK
   // Get iframe ID with evt.target.getIframe().id
   // Then access product video players with videoObjects[id]
   youtubePlayerReady: function(evt) {
    var iframeId = evt.target.getIframe().id;

    if (!videoObjects[iframeId]) {
     // No youtube player data
     return;
    }

    var obj = videoObjects[iframeId];
    var player = obj.videoPlayer;

    if (obj.options.style !== 'sound') {
     player.mute();
    }

    obj.parent.classList.remove('loading');
    obj.parent.classList.add('loaded');
    obj.parent.classList.add('video-interactable'); // Previously, video was only interactable after slide change

    // If we have an element, it is in the visible/first slide,
    // and is muted, play it
    if (this._isFirstSlide(iframeId) && obj.options.style !== 'sound') {
     player.playVideo();
    }
   },

   _isFirstSlide: function(id) {
    return this.cache.mainSlider.querySelector(selectors.startingSlide + ' ' + '#' + id);
   },

   youtubePlayerStateChange: function(evt) {
    var iframeId = evt.target.getIframe().id;
    var obj = videoObjects[iframeId];

    switch (evt.data) {
     case -1: // unstarted
      // Handle low power state on iOS by checking if
      // video is reset to unplayed after attempting to buffer
      if (obj.attemptedToPlay) {
       obj.parent.classList.add('video-interactable');
      }
      break;
     case 0: // ended
      if (obj && obj.options.loop === 'true') {
       obj.videoPlayer.playVideo();
      }
      break;
     case 3: // buffering
      obj.attemptedToPlay = true;
      break;
    }
   },

   initMp4Video: function(div) {
    videoObjects[div.id] = {
     id: div.id,
     type: 'mp4'
    };

    if (this._isFirstSlide(div.id)) {
     this.playMp4Video(div.id);
    }
   },

   stopVideos: function() {
    for (var [id, vid] of Object.entries(videoObjects)) {
     if (vid.videoPlayer) {
      if (typeof vid.videoPlayer.stopVideo === 'function') {
       vid.videoPlayer.stopVideo(); // YouTube player
      }
     } else if (vid.type === 'mp4') {
      this.stopMp4Video(vid.id); // MP4 player
     }
    }
   },

   _getVideoType: function(video) {
    return video.getAttribute('data-video-type');
   },

   _getVideoDivId: function(video) {
    return video.id;
   },

   playMp4Video: function(id) {
    var player = this.container.querySelector('#' + id);
    var playPromise = player.play();

    if (playPromise !== undefined) {
     playPromise.then(function() {
      // Playing as expected
     })
      .catch(function(error) {
       // Likely low power mode on iOS, show controls
       player.setAttribute('controls', '');
       player.closest(selectors.videoParent).setAttribute('data-video-style', 'unmuted');
      });
    }
   },

   stopMp4Video: function(id) {
    var player = this.container.querySelector('#' + id);
    if (player && typeof player.pause === 'function') {
     player.pause();
    }
   },

   /*============================================================================
     Product images
   ==============================================================================*/
   initImageZoom: function() {
    var container = this.container.querySelector(this.selectors.imageContainer);
    if (!container) {
     return;
    }
    var imageZoom = new theme.Photoswipe(container, this.sectionId);
    container.addEventListener('photoswipe:afterChange', function(evt) {
     if (this.flickity) {
      this.flickity.goToSlide(evt.detail.index);
     }
    }.bind(this));
   },

   getThumbIndex: function(target) {
    return target.dataset.index;
   },

   updateVariantImage: function(evt) {
    var variant = evt.detail.variant;
    var sizedImgUrl = theme.Images.getSizedImageUrl(variant.featured_media.preview_image.src, this.settings.imageSize);

    var newImage = this.container.querySelector('.product__thumb[data-id="' + variant.featured_media.id + '"]');
    var imageIndex = this.getThumbIndex(newImage);

    // If there is no index, slider is not initalized
    if (typeof imageIndex === 'undefined') {
     return;
    }

    // Go to that variant image's slide
    if (this.flickity) {
     this.flickity.goToSlide(imageIndex);
    }
   },

   initProductSlider: function(variant) {
    // Stop if only a single image, but add active class to first slide
    if (this.cache.mainSlider.querySelectorAll(selectors.slide).length <= 1) {
     var slide = this.cache.mainSlider.querySelector(selectors.slide);
     if (slide) {
      slide.classList.add('is-selected');
     }
     return;
    }

    // Destroy slider in preparation of new initialization
    if (this.flickity && typeof this.flickity.destroy === 'function') {
     this.flickity.destroy();
    }

    // If variant argument exists, slideshow is reinitializing because of the
    // image set feature enabled and switching to a new group.
    // currentSlideIndex
    if (!variant) {
     var activeSlide = this.cache.mainSlider.querySelector(selectors.startingSlide);
     this.settings.currentSlideIndex = this._slideIndex(activeSlide);
    }

    var mainSliderArgs = {
     dragThreshold: 25,
     adaptiveHeight: true,
     avoidReflow: true,
     initialIndex: this.settings.currentSlideIndex,
     childNav: this.cache.thumbSlider,
     childNavScroller: this.cache.thumbScroller,
     childVertical: this.cache.thumbSlider.dataset.position === 'beside',
     pageDots: true, // mobile only with CSS
     wrapAround: true,
     callbacks: {
      onInit: this.onSliderInit.bind(this),
      onChange: this.onSlideChange.bind(this)
     }
    };

    // Override default settings if image set feature enabled
    if (this.settings.imageSetName) {
     var imageSetArgs = this.imageSetArguments(variant);
     mainSliderArgs = Object.assign({}, mainSliderArgs, imageSetArgs);
     this.updateImageSetThumbs(mainSliderArgs.imageSet);
    }

    this.flickity = new theme.Slideshow(this.cache.mainSlider, mainSliderArgs);
   },

   onSliderInit: function(slide) {
    // If slider is initialized with image set feature active,
    // initialize any videos/media when they are first slide
    if (this.settings.imageSetName) {
     this.prepMediaOnSlide(slide);
    }
   },

   onSlideChange: function(index) {
    if (!this.flickity) return;

    var prevSlide = this.cache.mainSlider.querySelector('.product-main-slide[data-index="' + this.settings.currentSlideIndex + '"]');

    // If imageSetName exists, use a more specific selector
    var nextSlide = this.settings.imageSetName ?
     this.cache.mainSlider.querySelectorAll('.flickity-slider .product-main-slide')[index] :
     this.cache.mainSlider.querySelector('.product-main-slide[data-index="' + index + '"]');

    prevSlide.setAttribute('tabindex', '-1');
    nextSlide.setAttribute('tabindex', 0);

    // Pause any existing slide video/media
    this.stopMediaOnSlide(prevSlide);

    // Prep next slide video/media
    this.prepMediaOnSlide(nextSlide);

    // Update current slider index
    this.settings.currentSlideIndex = index;
   },

   stopMediaOnSlide(slide) {
    // Stop existing video
    var video = slide.querySelector(selectors.productVideo);
    if (video) {
     var videoType = this._getVideoType(video);
     var videoId = this._getVideoDivId(video);
     if (videoType === 'youtube') {
      if (videoObjects[videoId].videoPlayer) {
       videoObjects[videoId].videoPlayer.stopVideo();
       return;
      }
     } else if (videoType === 'mp4') {
      this.stopMp4Video(videoId);
      return;
     }
    }

    // Stop existing media
    var currentMedia = slide.querySelector(this.selectors.media);
    if (currentMedia) {
     currentMedia.dispatchEvent(
      new CustomEvent('mediaHidden', {
       bubbles: true,
       cancelable: true
      })
     );
    }
   },

   prepMediaOnSlide(slide) {
    var video = slide.querySelector(selectors.productVideo);
    if (video) {
     this.flickity.reposition();
     var videoType = this._getVideoType(video);
     var videoId = this._getVideoDivId(video);
     if (videoType === 'youtube') {
      if (videoObjects[videoId].videoPlayer && videoObjects[videoId].options.style !== 'sound') {
       videoObjects[videoId].videoPlayer.playVideo();
       return;
      }
     } else if (videoType === 'mp4') {
      this.playMp4Video(videoId);
     }
    }

    var nextMedia = slide.querySelector(this.selectors.media);
    if (nextMedia) {
     nextMedia.dispatchEvent(
      new CustomEvent('mediaVisible', {
       bubbles: true,
       cancelable: true
      })
     );
     slide.querySelector('.shopify-model-viewer-ui__button').setAttribute('tabindex', 0);
     slide.querySelector('.product-single__close-media').setAttribute('tabindex', 0);
    }
   },

   _slideIndex: function(el) {
    return el.getAttribute('data-index');
   },

   /*============================================================================
     Products when in quick view modal
   ==============================================================================*/
   openModalProduct: function() {
    var initialized = false;

    if (!this.settings.modalInit) {
     this.blocksHolder = this.container.querySelector(selectors.blocksHolder);

     // If blocks are already rendered (no placeholder), skip fetching
     if (!this.blocksHolder) {
      this.cacheElements();
      this.formSetup();
      this.updateModalProductInventory();

      if (Shopify && Shopify.PaymentButton) {
       Shopify.PaymentButton.init();
      }

      theme.collapsibles.init(this.container);

      document.dispatchEvent(new CustomEvent('quickview:loaded', {
       detail: {
        productId: this.productId
       }
      }));
     } else {
      var url = this.blocksHolder.dataset.url;

      fetch(url).then(function(response) {
       return response.text();
      }).then(function(html) {
       var parser = new DOMParser();
       var doc = parser.parseFromString(html, 'text/html');
       var blocks = doc.querySelector(selectors.blocks);

       if (!blocks) {
        console.warn('Quick view: could not load product blocks for', url);
        this.blocksHolder.innerHTML = '';
        this.blocksHolder.classList.add('product-form-holder--loaded');
        return;
       }

       // Because the same product could be opened in quick view
       // on the page we load the form elements from, we need to
       // update any `id`, `for`, and `form` attributes
       blocks.querySelectorAll('[id]').forEach(el => {
        // Update input `id`
        var val = el.getAttribute('id');
        el.setAttribute('id', val + '-modal');

        // Update related label if it exists
        var label = blocks.querySelector(`[for="${val}"]`);
        if (label) {
         label.setAttribute('for', val + '-modal');
        }

        // Update any collapsible elements
        var collapsibleTrigger = blocks.querySelector(`[aria-controls="${val}"]`);
        if (collapsibleTrigger) {
         collapsibleTrigger.setAttribute('aria-controls', val + '-modal');
        }
       });

       // Update any elements with `form` attribute.
       // Form element already has `-modal` appended
       var form = blocks.querySelector(this.selectors.form);
       if (form) {
        var formId = form.getAttribute('id');
        blocks.querySelectorAll('[form]').forEach(el => {
         el.setAttribute('form', formId);
        });
       }

       this.blocksHolder.innerHTML = '';
       this.blocksHolder.append(blocks);
       this.blocksHolder.classList.add('product-form-holder--loaded');

       this.cacheElements();

       this.formSetup();
       this.updateModalProductInventory();

       if (Shopify && Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
       }

       // Re-hook up collapsible box triggers
       theme.collapsibles.init(this.container);

       document.dispatchEvent(new CustomEvent('quickview:loaded', {
        detail: {
         productId: this.productId
        }
       }));
      }.bind(this));
     } // end else (blocksHolder exists)

     this.productSetup();
     this.videoSetup();

     // Enable product slider in quick view
     // 1. with image sets enabled, make sure we have this.variants before initializing
     // 2. initialize normally, form data not required
     if (this.settings.imageSetName) {
      if (this.variants) {
       this.initProductSlider();
      } else {
       document.addEventListener('quickview:loaded', function(evt) {
        if (evt.detail.productId === this.productId) {
         this.initProductSlider();
        }
       }.bind(this));
      }
     } else {
      this.initProductSlider();
     }
     this.customMediaListners();
     this.addIdToRecentlyViewed();
     this.settings.modalInit = true;
    } else {
     initialized = true;
    }

    AOS.refreshHard();

    document.dispatchEvent(new CustomEvent('quickview:open', {
     detail: {
      initialized: initialized,
      productId: this.productId
     }
    }));
   },

   // Recommended products load via JS and don't add variant inventory to the
   // global variable that we later check. This function scrapes a data div
   // to get that info and manually add the values.
   updateModalProductInventory: function() {
    window.inventories = window.inventories || {};
    this.container.querySelectorAll('.js-product-inventory-data').forEach(el => {
     var productId = el.dataset.productId;
     window.inventories[productId] = {};

     el.querySelectorAll('.js-variant-inventory-data').forEach(el => {
      window.inventories[productId][el.dataset.id] = {
       'quantity': el.dataset.quantity,
       'policy': el.dataset.policy,
       'incoming': el.dataset.incoming,
       'next_incoming_date': el.dataset.date
      }
     });
    });
   },

   closeModalProduct: function() {
    this.stopVideos();
   },

   /*============================================================================
     Product media (3D)
   ==============================================================================*/
   initModelViewerLibraries: function() {
    var modelViewerElements = this.container.querySelectorAll(this.selectors.media);
    if (modelViewerElements.length < 1) return;

    theme.ProductMedia.init(modelViewerElements, this.sectionId);
   },

   initShopifyXrLaunch: function() {
    document.addEventListener(
     'shopify_xr_launch',
     function() {
      var currentMedia = this.container.querySelector(
       this.selectors.productMediaWrapper +
       ':not(.' +
       self.classes.hidden +
       ')'
      );
      currentMedia.dispatchEvent(
       new CustomEvent('xrLaunch', {
        bubbles: true,
        cancelable: true
       })
      );
     }.bind(this)
    );
   },

   customMediaListners: function() {
    document.querySelectorAll(this.selectors.closeMedia).forEach(el => {
     el.addEventListener('click', function() {
      var slide = this.cache.mainSlider.querySelector(selectors.currentSlide);
      var media = slide.querySelector(this.selectors.media);
      if (media) {
       media.dispatchEvent(
        new CustomEvent('mediaHidden', {
         bubbles: true,
         cancelable: true
        })
       );
      }
     }.bind(this))
    });

    var modelViewers = this.container.querySelectorAll('model-viewer');
    if (modelViewers.length) {
     modelViewers.forEach(el => {
      el.addEventListener('shopify_model_viewer_ui_toggle_play', function(evt) {
       this.mediaLoaded(evt);
      }.bind(this));

      el.addEventListener('shopify_model_viewer_ui_toggle_pause', function(evt) {
       this.mediaUnloaded(evt);
      }.bind(this));
     });
    }
   },

   mediaLoaded: function(evt) {
    this.container.querySelectorAll(this.selectors.closeMedia).forEach(el => {
     el.classList.remove(classes.hidden);
    });

    if (this.flickity) {
     this.flickity.setDraggable(false);
    }
   },

   mediaUnloaded: function(evt) {
    this.container.querySelectorAll(this.selectors.closeMedia).forEach(el => {
     el.classList.add(classes.hidden);
    });

    if (this.flickity) {
     this.flickity.setDraggable(true);
    }
   },

   onUnload: function() {
    theme.ProductMedia.removeSectionModels(this.sectionId);

    if (this.flickity && typeof this.flickity.destroy === 'function') {
     this.flickity.destroy();
    }
   }
  });

  return Product;
 })();


  var registerProductSections = function() {
    if (window.theme && theme.sections) {
      theme.sections.register('product', theme.Product);
      theme.sections.register('store-availability', theme.StoreAvailability);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerProductSections);
  } else {
    registerProductSections();
  }
})();
